/**
 * ImagePickerModal — Universal image picker with File Manager + Pexels tabs.
 * Use this anywhere an image URL input needs a "Browse" button.
 *
 * Usage:
 *   <ImagePickerModal isOpen={open} onClose={() => setOpen(false)} onSelect={url => setImageUrl(url)} />
 */

import React, { useState, useEffect } from 'react';
import UnifiedFileManager from '../modules/media/UnifiedFileManager';
import StockPhotosBrowser from '../modules/media/StockPhotosBrowser';

type TabType = 'files' | 'pexels';

interface ImagePickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (url: string) => void;
    title?: string;
    language?: 'en' | 'sv';
}

const ImagePickerModal: React.FC<ImagePickerModalProps> = ({
    isOpen,
    onClose,
    onSelect,
    title = 'Select Image',
    language = 'en'
}) => {
    const [activeTab, setActiveTab] = useState<TabType>('files');

    // Reset tab when modal opens
    useEffect(() => {
        if (isOpen) setActiveTab('files');
    }, [isOpen]);

    if (!isOpen) return null;

    const handleFileSelect = (file: any) => {
        const url = file.url || file.path || '';
        if (url) {
            onSelect(url);
            onClose();
        }
    };

    const handleStockSelect = (file: any) => {
        const url = file.url || file.path || '';
        if (url) {
            onSelect(url);
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onClose}>
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Modal */}
            <div
                className="relative w-[90vw] max-w-5xl h-[80vh] bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 flex flex-col overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <span className="material-icons text-indigo-400">image</span>
                        <h2 className="text-lg font-bold text-white">{title}</h2>
                    </div>

                    {/* Tabs */}
                    <div className="flex items-center gap-1 bg-slate-700/50 rounded-xl p-1">
                        <button
                            onClick={() => setActiveTab('files')}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${activeTab === 'files'
                                    ? 'bg-indigo-600 text-white shadow-lg'
                                    : 'text-slate-400 hover:text-white hover:bg-slate-600'
                                }`}
                        >
                            <span className="material-icons text-sm">folder</span>
                            {language === 'sv' ? 'Filhanterare' : 'File Manager'}
                        </button>
                        <button
                            onClick={() => setActiveTab('pexels')}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${activeTab === 'pexels'
                                    ? 'bg-purple-600 text-white shadow-lg'
                                    : 'text-slate-400 hover:text-white hover:bg-slate-600'
                                }`}
                        >
                            <span className="material-icons text-sm">photo_library</span>
                            Pexels
                        </button>
                    </div>

                    {/* Close */}
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                    >
                        <span className="material-icons">close</span>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden">
                    {activeTab === 'files' ? (
                        <UnifiedFileManager
                            theme="dark"
                            language={language}
                            onSelect={handleFileSelect}
                            initialSource="local"
                        />
                    ) : (
                        <StockPhotosBrowser
                            theme="dark"
                            language={language}
                            onSelect={handleStockSelect}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImagePickerModal;
